import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../api/client';
import { blogApi, generationApi, type BlogTopic, type BlogTopicStatus, type SaveBlogTopic } from '../../api/blog';
import BlogNav from './BlogNav';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const STATUS_BADGE: Record<BlogTopicStatus, string> = {
  Queued: 'badge badge--info',
  InProgress: 'badge badge--warning',
  Used: 'badge badge--published',
  Rejected: 'badge',
};

const EMPTY: SaveBlogTopic = {
  key: null,
  title: '',
  brief: '',
  targetKeywords: [],
  category: '',
  priority: 70,
  preferredMonths: [],
};

/**
 * The backlog the generator writes from.
 *
 * The list is shown in the order it will be taken — in season first, then by priority, then by
 * age — so the top row is tomorrow's article. That is the point of the page: whoever runs the blog
 * decides what it is about by editing this list, not by editing a prompt.
 */
export default function BlogTopicsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [status, setStatus] = useState<BlogTopicStatus | ''>('Queued');
  const [editing, setEditing] = useState<BlogTopic | null>(null);
  const [form, setForm] = useState<SaveBlogTopic>(EMPTY);
  const [keywords, setKeywords] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: topics, isPending } = useQuery({
    queryKey: ['blog-topics', status],
    queryFn: () => generationApi.topics(status),
  });

  const { data: tags } = useQuery({ queryKey: ['blog-tags'], queryFn: blogApi.tags });

  function fail(e: unknown) {
    setError(e instanceof ApiError ? e.message : 'Something went wrong.');
  }

  function reset() {
    setEditing(null);
    setForm(EMPTY);
    setKeywords('');
    setError(null);
  }

  async function refresh() {
    setError(null);
    await queryClient.invalidateQueries({ queryKey: ['blog-topics'] });
    await queryClient.invalidateQueries({ queryKey: ['blog-automation'] });
  }

  const save = useMutation({
    mutationFn: () => {
      const topic: SaveBlogTopic = {
        ...form,
        targetKeywords: keywords
          .split(',')
          .map((keyword) => keyword.trim())
          .filter(Boolean),
      };

      return editing ? generationApi.updateTopic(editing.id, topic) : generationApi.createTopic(topic);
    },
    onSuccess: async () => {
      reset();
      await refresh();
    },
    onError: fail,
  });

  const reject = useMutation({ mutationFn: generationApi.rejectTopic, onSuccess: refresh, onError: fail });
  const requeue = useMutation({ mutationFn: generationApi.requeueTopic, onSuccess: refresh, onError: fail });
  const remove = useMutation({ mutationFn: generationApi.removeTopic, onSuccess: refresh, onError: fail });

  const write = useMutation({
    mutationFn: (id: string) => generationApi.generate(id),
    onSuccess: (run) => navigate(`/blog/automation?run=${run.id}`),
    onError: fail,
  });

  function edit(topic: BlogTopic) {
    setEditing(topic);
    setForm({
      key: topic.key,
      title: topic.title,
      brief: topic.brief,
      targetKeywords: topic.targetKeywords,
      category: topic.category,
      priority: topic.priority,
      preferredMonths: topic.preferredMonths,
    });
    setKeywords(topic.targetKeywords.join(', '));
    setError(null);
    document.getElementById('topicTitle')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function toggleMonth(month: number) {
    setForm((current) => ({
      ...current,
      preferredMonths: current.preferredMonths.includes(month)
        ? current.preferredMonths.filter((m) => m !== month)
        : [...current.preferredMonths, month].sort((a, b) => a - b),
    }));
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }

  return (
    <>
      <BlogNav />

      <section className="card">
        <h2>Topics</h2>
        <p className="muted">
          What the generator will write about, in the order it will write it: topics in season first, then by priority,
          then the oldest. The brief is what the writer is told the article must cover — it is the most direct way to
          shape an article before it exists.
        </p>

        <div className="toolbar">
          <select className="field__input" style={{ maxWidth: '12rem' }} value={status} onChange={(e) => setStatus(e.target.value as BlogTopicStatus | '')}>
            <option value="Queued">Waiting</option>
            <option value="Used">Written</option>
            <option value="Rejected">Rejected</option>
            <option value="InProgress">Being written</option>
            <option value="">All</option>
          </select>
          {topics && <span className="muted">{topics.length} topic(s)</span>}
        </div>

        {error && <p className="status-error">{error}</p>}
        {isPending && <p className="muted">Loading…</p>}

        {topics && topics.length > 0 && (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Topic</th>
                  <th>Tag</th>
                  <th>Priority</th>
                  <th>Season</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {topics.map((topic, index) => (
                  <tr key={topic.id}>
                    <td className="muted">{index + 1}</td>
                    <td>
                      <strong>{topic.title}</strong>
                      <div className="muted">{topic.brief}</div>
                      {topic.lastError && <div className="status-error">Last attempt failed: {topic.lastError}</div>}
                    </td>
                    <td className="muted">{topic.category || '—'}</td>
                    <td>{topic.priority}</td>
                    <td className="muted">
                      {topic.preferredMonths.length === 0 ? 'Any time' : topic.preferredMonths.map((m) => MONTHS[m - 1]).join(', ')}
                    </td>
                    <td>
                      <span className={STATUS_BADGE[topic.status]}>{topic.status}</span>
                      <div className="muted">{topic.source}</div>
                    </td>
                    <td>
                      <div className="toolbar">
                        {topic.status === 'Queued' && (
                          <>
                            <button
                              type="button"
                              className="btn btn--small"
                              disabled={write.isPending}
                              title="Writes this topic now. Lands as a draft."
                              onClick={() => write.mutate(topic.id)}
                            >
                              Write now
                            </button>
                            <button type="button" className="btn btn--small" onClick={() => edit(topic)}>
                              Edit
                            </button>
                            <button type="button" className="btn btn--small" disabled={reject.isPending} onClick={() => reject.mutate(topic.id)}>
                              Reject
                            </button>
                          </>
                        )}
                        {topic.status === 'Rejected' && (
                          <>
                            <button type="button" className="btn btn--small" disabled={requeue.isPending} onClick={() => requeue.mutate(topic.id)}>
                              Back to the queue
                            </button>
                            <button type="button" className="btn btn--small" disabled={remove.isPending} onClick={() => remove.mutate(topic.id)}>
                              Delete
                            </button>
                          </>
                        )}
                        {topic.status === 'Used' && topic.usedByPostId && (
                          <button type="button" className="btn btn--small" onClick={() => navigate(`/blog/${topic.usedByPostId}`)}>
                            Open the article
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {topics && topics.length === 0 && <p className="muted">Nothing here.</p>}
      </section>

      <section className="card">
        <h3>{editing ? 'Edit topic' : 'New topic'}</h3>
        <form onSubmit={onSubmit}>
          <div className="field">
            <label className="field__label" htmlFor="topicTitle">
              Working title, as people would search for it
            </label>
            <input
              id="topicTitle"
              className="field__input"
              value={form.title}
              maxLength={200}
              required
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="topicBrief">
              Brief — what the article must cover, which questions it answers, what people get wrong
            </label>
            <textarea
              id="topicBrief"
              className="field__input"
              rows={5}
              value={form.brief}
              maxLength={2000}
              required
              onChange={(e) => setForm({ ...form, brief: e.target.value })}
            />
            <span className="field__counter">{form.brief.length} / 2000</span>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="topicKeywords">
              Search phrases, separated by commas
            </label>
            <input id="topicKeywords" className="field__input" value={keywords} onChange={(e) => setKeywords(e.target.value)} />
          </div>

          <div className="split">
            <div className="field">
              <label className="field__label" htmlFor="topicCategory">
                Tag
              </label>
              <select
                id="topicCategory"
                className="field__input"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                <option value="">—</option>
                {tags?.map((tag) => (
                  <option key={tag.id} value={tag.slug}>
                    {tag.nameBg}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label className="field__label" htmlFor="topicPriority">
                Priority, 1–100
              </label>
              <input
                id="topicPriority"
                className="field__input"
                type="number"
                min={1}
                max={100}
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: Number(e.target.value) || 1 })}
              />
            </div>
          </div>

          <fieldset className="field">
            <legend className="field__label">Best published in (none means any time)</legend>
            <div className="toolbar">
              {MONTHS.map((name, index) => (
                <label key={name} className="check">
                  <input
                    type="checkbox"
                    checked={form.preferredMonths.includes(index + 1)}
                    onChange={() => toggleMonth(index + 1)}
                  />{' '}
                  {name}
                </label>
              ))}
            </div>
          </fieldset>

          <div className="toolbar">
            <button type="submit" className="btn btn--primary" disabled={save.isPending}>
              {editing ? 'Save' : 'Add to the queue'}
            </button>
            {editing && (
              <button type="button" className="btn" onClick={reset}>
                Cancel
              </button>
            )}
          </div>
        </form>
      </section>
    </>
  );
}
